function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
import React, { forwardRef, useMemo, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { InternalProvider, attachSubComponents, isComponentType, useCombinedRefs, useIsBrowser, useDisableBackgroundScroll } from '@soluto-private/mx-asurion-ui-react';
import { DismissButton, Header, ModalContainer, Overlay, Subtitle, Title } from './elements';
import { Content } from './Content';
import { Footer } from './Footer';
import { ModalContext } from './ModalContext';
import { useTrapFocus } from './useTrapFocus';
export const Modal = attachSubComponents('Modal', /*#__PURE__*/forwardRef(({
  children,
  ariaLabel,
  isOpen,
  isSticky,
  onClick,
  onCloseModal,
  portalContainer,
  ...props
}, fRef) => {
  // innerRef.current is undefined until SECOND render, so we need to force rerender
  const [, setDidUpdateRef] = React.useState(false);
  const innerRef = useRef();
  const combinedRef = useCombinedRefs(fRef, innerRef, () => setDidUpdateRef(true));
  useTrapFocus(innerRef, isOpen);
  useDisableBackgroundScroll(isOpen, portalContainer);
  const {
    hasHeader,
    hasFooter,
    isHeroHeader
  } = useMemo(() => {
    const childrenAsArray = React.Children.toArray(children);
    const findChildComponent = component => childrenAsArray.find(el => isComponentType(el, component));
    const header = findChildComponent(Modal.Header);
    return {
      hasHeader: !!header,
      hasFooter: !!findChildComponent(Modal.Footer),
      isHeroHeader: !!(header !== null && header !== void 0 && header.props.heroImage)
    };
  }, [children]);
  useEffect(() => {
    const onEscapeCloseModal = e => {
      if (e.key === 'Escape') onCloseModal();
    };
    document.addEventListener('keydown', onEscapeCloseModal);
    return () => {
      document.removeEventListener('keydown', onEscapeCloseModal);
    };
  }, []);
  if (!useIsBrowser()) return null;
  return /*#__PURE__*/createPortal( /*#__PURE__*/React.createElement(InternalProvider, null, /*#__PURE__*/React.createElement(Overlay, {
    isOpen: isOpen,
    onClick: onCloseModal
  }, /*#__PURE__*/React.createElement(ModalContext.Provider, {
    value: {
      hasHeader,
      hasFooter,
      isSticky,
      isHeroHeader
    }
  }, /*#__PURE__*/React.createElement(ModalContainer, _extends({
    ref: combinedRef,
    isOpen: isOpen,
    "aria-modal": "true",
    "data-aui": "modal",
    role: "dialog",
    "aria-label": ariaLabel,
    onClick: e => {
      onClick === null || onClick === void 0 ? void 0 : onClick(e);
      e.stopPropagation();
    }
  }, props, {
    isSticky: isSticky
  }), /*#__PURE__*/React.createElement(DismissButton, {
    onClick: onCloseModal
  }), children)))), portalContainer || document.body);
}), {
  Header,
  Title,
  Subtitle,
  Content,
  Footer
});